import React, { createContext, useContext, useState, useMemo } from 'react';
import type { FC, ReactNode } from 'react';

type ObjectCommentContextValue = {
	expanded: boolean;
	source?: string;
	width?: string;
};

type ObjectCommentDispatchContextValue = {
	expandCommentContainer: (source?: string) => void;
	closeCommentContainer: () => void;
	setWrapperWidth: (width?: string | number) => void;
};

export const ObjectCommentDispatchContext = createContext<ObjectCommentDispatchContextValue>({
	expandCommentContainer: () => {},
	closeCommentContainer: () => {},
	setWrapperWidth: () => {},
});
ObjectCommentDispatchContext.displayName = 'ObjectCommentDispatchContext';

export const ObjectCommentContext = createContext<ObjectCommentContextValue>({
	expanded: false,
	source: undefined,
	width: undefined,
});
ObjectCommentContext.displayName = 'ObjectCommentContext';

type ObjectCommentProviderProps = {
	children: ReactNode;
};

export const ObjectCommentProvider: FC<ObjectCommentProviderProps> = ({ children }) => {
	const [expanded, setExpanded] = useState(false);
	const [source, setSource] = useState<string | undefined>(undefined);
	const [width, setWidth] = useState<string | undefined>(undefined);

	const providerValue = useMemo(
		() => ({
			expanded,
			source,
			width,
		}),
		[expanded, source, width],
	);

	const dispatchProviderValue = useMemo(
		() => ({
			expandCommentContainer: (source?: string) => {
				setExpanded(true);
				setSource(source);
			},
			closeCommentContainer: () => {
				setSource(undefined);
				setExpanded(false);
			},
			setWrapperWidth: (width?: string | number) => {
				setWidth(width ? `${width}px` : '100%');
			},
		}),
		[setExpanded, setSource, setWidth],
	);

	return (
		<ObjectCommentContext.Provider value={providerValue}>
			<ObjectCommentDispatchContext.Provider value={dispatchProviderValue}>
				{children}
			</ObjectCommentDispatchContext.Provider>
		</ObjectCommentContext.Provider>
	);
};

export const useObjectCommentContext = () => {
	return useContext(ObjectCommentContext);
};

export const useObjectCommentDispatchContext = () => {
	return useContext(ObjectCommentDispatchContext);
};
